// matchBoardApi.ts — the ONE place the match board's API-shape assumptions live.
//
// ERP expansion chunk 2, phase 6 (frontend). The backend
// (api/src/procurement/match-board-router.js + match-board-service.js) shipped
// first; the shapes below are read directly off that code, not guessed.
//
// Money shapes differ BY SIDE, deliberately: the PO side's sums are computed
// server-side and cross as NUMBERS (recomputeBilling's convention); the invoice
// side's amount_cents keeps the queue's bigint-as-string shape. formatCents
// takes both, so nothing here retypes a column.
//
// Access: requireCapability('procurement', 'admin') — the purchasing side of
// billing. The invoice queue and review screens stay can_financial; this board
// can stage a match but the money act, confirm, is not reachable from it.

import { useApi } from '../apiHelpers';

/** One purchase-order line still owed billing (or carrying pending links). */
export interface BoardPoLine {
  id: string;
  purchase_order_id: string;
  line_number: number;
  line_type: 'stock' | 'service' | 'charge';
  status: string;
  quantity_expected: number | null;
  quantity_received: number | null;
  unit_cost_cents: number | null;
  po_number: string | null;
  po_status: string;
  vendor_id: string | null;
  vendor_name: string | null;
  sku: string | null;
  part_description: string | null;
  // From the catalog_item_names view (migration 068).
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  mpn: string | null;
  billed_amount_cents: number;
  pending_cents: number;
  line_value_cents: number;
  remaining_to_bill_cents: number;
  /** DISTINCT review invoices pointing at this line. Can exceed
   *  pending_invoice_numbers.length: unnumbered invoices count here but
   *  cannot be named there. */
  pending_invoice_count: number;
  /** Null-filtered server-side, so every entry is a real number. */
  pending_invoice_numbers: string[];
  /** billed + pending exceeds the line's value: several invoices are
   *  converging on one commitment past what it is worth. */
  over_pending: boolean;
}

/** One line of a still-in-review invoice that names no order. */
export interface BoardInvoiceLine {
  id: string;
  vendor_invoice_id: string;
  line_number: number;
  description: string | null;
  part_number_observed: string | null;
  mfg_part_number_observed: string | null;
  quantity: string | null;            // NUMERIC as string
  unit_price_cents: string | null;    // NUMERIC as string, sub-cent digits legal
  amount_cents: string | null;        // bigint as string; negative on credits
  line_type: 'stock' | 'service' | 'charge';
  charge_type: string | null;
  invoice_number: string | null;
  invoice_date: string | null;        // YYYY-MM-DD
  is_credit_memo: boolean;
  vendor_id: string | null;
  vendor_name: string | null;
  vendor_observed: string | null;
}

export interface MatchBoard {
  po_lines: BoardPoLine[];
  invoice_lines: BoardInvoiceLine[];
}

/** What the buyer door answers with: enough to toast honestly. */
export interface UploadedInvoice {
  invoice: {
    id: string;
    invoice_number: string | null;
    vendor_observed: string | null;
    match_method: string | null;
    needs_attention: boolean;
    status: string;
    attention_reasons: Array<{ code: string; message: string }> | null;
  };
  lines: Array<{ purchase_order_line_id: string | null }>;
}

export function useMatchBoardApi() {
  const api = useApi();

  return {
    getBoard: (): Promise<MatchBoard> => api.get('/procurement/match-board'),

    /** The buyer door: "here is the invoice for THIS order." The order id is a
     *  matcher HINT — printed numbers on the paper still outrank it. */
    uploadForOrder: (purchaseOrderId: string, file: File): Promise<UploadedInvoice> => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purchase_order_id', purchaseOrderId);
      return api.post('/procurement/match-board/upload', { body: fd, silent: true });
    },

    /** Pair one invoice line with one PO line. Delegates to setLineMatch
     *  server-side, so the financial screen's guards answer here too. */
    link: (invoiceLineId: string, purchaseOrderLineId: string): Promise<{ line: unknown }> =>
      api.post('/procurement/match-board/link', {
        body: { invoice_line_id: invoiceLineId, purchase_order_line_id: purchaseOrderLineId },
      }),
  };
}
